import React, { useRef } from 'react';
import { Loader2, Save, Upload, Sparkles } from 'lucide-react';
import { Button } from '../../../ui/button';
import { Input } from '../../../ui/input';
import { Label } from '../../../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../../ui/dialog';
import { SwitchToggle } from './SwitchToggle';
import { StockStatusToggles } from './StockStatusToggles';
import { RoomTypeSelect } from '../RoomTypeSelect';
import { ColorImage } from '../ColorImage';

export const ColorDialog = ({
  open, onOpenChange, color, setColor, onSave, saving,
  uploadingImage, onUploadImage,
  autoGenerateViz, setAutoGenerateViz, vizRoomType, setVizRoomType, generatingViz,
}) => {
  const fileRef = useRef(null);
  const set = (key) => (e) => setColor(prev => ({ ...prev, [key]: e.target.value }));
  const c = color || {};

  const handleCancel = () => { onOpenChange(false); setAutoGenerateViz(true); };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="color-dialog">
        <DialogHeader>
          <DialogTitle>{c.id ? 'Edit Color' : 'Add New Color'}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div>
            <Label>Color Name *</Label>
            <Input value={c.name || ''} onChange={set('name')} placeholder="e.g., White, Ivory, Charcoal" className="mt-1" data-testid="color-name-input" />
          </div>
          <div>
            <Label>Color Code</Label>
            <Input value={c.code || ''} onChange={set('code')} placeholder="e.g., 001, WHT, #FFFFFF" className="mt-1" data-testid="color-code-input" />
          </div>
          <div>
            <Label>Image URL</Label>
            <div className="flex gap-2 mt-1">
              <Input value={c.image_url || ''} onChange={set('image_url')} placeholder="https://... (fabric swatch image)" className="flex-1" />
              <Button type="button" variant="outline" size="sm" onClick={() => fileRef.current?.click()} disabled={uploadingImage} className="gap-1" data-testid="color-upload-btn">
                {uploadingImage ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                Upload
              </Button>
              <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) onUploadImage(f); }} />
            </div>
            {c.image_url && (
              <div className="mt-2">
                <ColorImage src={c.image_url} alt="Preview" w={256} eager className="w-24 h-24 rounded-lg border overflow-hidden" />
              </div>
            )}
          </div>
          <div>
            <Label>Hex Color (fallback)</Label>
            <div className="flex gap-2 mt-1">
              <Input type="color" value={c.hex_color || '#FFFFFF'} onChange={set('hex_color')} className="w-16 h-10 p-1" />
              <Input value={c.hex_color || ''} onChange={set('hex_color')} placeholder="#FFFFFF" className="flex-1" />
            </div>
          </div>
          <div className="border-t pt-4 space-y-3">
            <SwitchToggle
              label="Visible Online" hint="Show in frontend configurator"
              checked={c.is_visible_online !== false}
              onToggle={() => setColor(prev => ({ ...prev, is_visible_online: !prev?.is_visible_online }))}
              testId="color-visible-toggle"
            />
            <SwitchToggle
              label="On Sale" hint="Display sale badge" onColor="bg-red-500"
              checked={!!c.is_on_sale}
              onToggle={() => setColor(prev => ({ ...prev, is_on_sale: !prev?.is_on_sale }))}
              testId="color-sale-toggle"
            />
            <StockStatusToggles value={c.stock_status || null} onChange={(v) => setColor(prev => ({ ...prev, stock_status: v }))} discontinued={!!c.discontinued} onDiscontinuedChange={(v) => setColor(prev => ({ ...prev, discontinued: v }))} testIdPrefix="color" />
          </div>

          {!c.id && (
            <div className="border-t pt-4">
              <SwitchToggle
                label={<span className="flex items-center gap-2"><Sparkles className="w-4 h-4 text-amber-600" /> Auto-generate AI Visualization</span>}
                hint="Generate a product image for this color using AI" onColor="bg-amber-500"
                checked={autoGenerateViz} onToggle={() => setAutoGenerateViz(!autoGenerateViz)}
                testId="auto-generate-viz-toggle"
              />
              {autoGenerateViz && (
                <div className="mt-3 space-y-2">
                  <Label className="text-xs text-neutral-500">Room scene</Label>
                  <RoomTypeSelect value={vizRoomType} onChange={setVizRoomType} className="w-full" testId="add-color-room-type-select" />
                  <p className="text-xs text-amber-600 bg-amber-50 p-2 rounded">A visualization in this room will be generated in the background after saving</p>
                </div>
              )}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={handleCancel}>Cancel</Button>
          <Button onClick={onSave} disabled={saving || generatingViz} className="bg-amber-600 hover:bg-amber-700" data-testid="color-save-btn">
            {saving ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Saving...</>
              : generatingViz ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Generating AI...</>
              : <><Save className="w-4 h-4 mr-2" /> Save Color</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ColorDialog;
